const SELECTORS = {
  trigger: "[data-md-trigger]",
  closeBtn: "[data-md-close]",
  panel: "[data-md-panel]",
  mobileDrawer: "[data-md-mobile]",
  accItem: "[data-md-acc-item]",
  accBtn: "[data-md-acc-btn]",
  accBody: "[data-md-acc-body]",
};

export const MD_EVENTS = {
  OPENED: "material-drawer:opened",
  CLOSED: "material-drawer:closed",
};

class MaterialDrawer extends HTMLElement {
  #sidebarObserver = null;

  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.close = this.close.bind(this);
  }

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[MaterialDrawer:init]", error);
    }
  }

  init() {
    this.#mirrorToMobile();
    this.#bindElements();
    this.#bindEvents();
    this.#observeSidebar();
  }

  #mirrorToMobile() {
    const source = this.querySelector("[data-md-panel-content]");
    const target = this.querySelector("[data-md-mobile-content]");
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
  }

  #bindElements() {
    this.trigger = this.querySelector(SELECTORS.trigger);
    this.closeBtn = this.querySelector(SELECTORS.closeBtn);
    this.panel = this.querySelector(SELECTORS.panel);
    this.mobileDrawer = this.querySelector(SELECTORS.mobileDrawer);
  }

  #bindEvents() {
    this.trigger?.addEventListener("click", () => this.#onTriggerClick());
    this.closeBtn?.addEventListener("click", this.close);
    this.querySelectorAll(SELECTORS.accBtn).forEach((btn) => {
      btn.addEventListener("click", (e) => this.#onAccBtnClick(e));
    });
  }

  #observeSidebar() {
    const sidebar = this.closest(".article-layout__sidebar");
    if (!sidebar) return;
    this.#sidebarObserver = new ResizeObserver(() => {
      if (!this.classList.contains("panel-open")) return;
      if (!window.matchMedia("(min-width: 768px)").matches) {
        this.close();
        return;
      }
      this.#repositionPanel();
    });
    this.#sidebarObserver.observe(sidebar);
  }

  #getSidebarRect() {
    const sidebar = this.closest(".article-layout__sidebar");
    return (sidebar ?? this).getBoundingClientRect();
  }

  #getHeaderHeight() {
    return (
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height"
        )
      ) || 0
    );
  }

  #repositionPanel() {
    const rect = this.#getSidebarRect();
    const headerHeight = this.#getHeaderHeight();
    Object.assign(this.panel.style, {
      top: headerHeight + "px",
      bottom: "0",
      left: rect.left + "px",
      width: rect.width + "px",
    });
  }

  #onTriggerClick() {
    if (window.matchMedia("(min-width: 768px)").matches) {
      this.openPanel();
    } else {
      this.#toggleMobileDrawer();
    }
  }

  #toggleMobileDrawer() {
    if (this.mobileDrawer?.hasAttribute("hidden")) {
      this.mobileDrawer.removeAttribute("hidden");
    } else {
      this.mobileDrawer?.setAttribute("hidden", "");
    }
  }

  openPanel() {
    this.#repositionPanel();
    this.classList.add("panel-open");
    this.panel?.setAttribute("aria-hidden", "false");
    this.panel?.focus();
    document.addEventListener("keydown", this.onKeydown);
    document.dispatchEvent(
      new CustomEvent(MD_EVENTS.OPENED, { detail: { source: this } })
    );
  }

  close() {
    this.classList.remove("panel-open");
    this.panel?.setAttribute("aria-hidden", "true");
    ["top", "bottom", "left", "width"].forEach((p) =>
      this.panel?.style.removeProperty(p)
    );
    document.removeEventListener("keydown", this.onKeydown);
    this.trigger?.focus();
    document.dispatchEvent(
      new CustomEvent(MD_EVENTS.CLOSED, { detail: { source: this } })
    );
  }

  onKeydown(e) {
    if (e.key === "Escape") this.close();
  }

  #onAccBtnClick(e) {
    const item = e.currentTarget.closest(SELECTORS.accItem);
    if (!item) return;
    const body = item.querySelector(SELECTORS.accBody);
    if (!body) return;

    const isOpen = item.classList.contains("is-open");
    if (isOpen) {
      item.classList.remove("is-open");
      body.style.height = "0";
    } else {
      item.classList.add("is-open");
      body.style.height = `${body.scrollHeight}px`;
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    document.removeEventListener("keydown", this.onKeydown);
    this.#sidebarObserver?.disconnect();
  }
}

if (!customElements.get("material-drawer")) {
  customElements.define("material-drawer", MaterialDrawer);
}
